import Container from "@client/content/Containers/container";
import FormContainer from "@client/content/controls/Form/container";
import { getContainerProps, getRootContainerProps } from "@client/content/lib/helper";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { type FormButtonProps, type FormFieldInfo } from "@client/controls/Form/component";
import NumberInput from "@client/controls/Inputs/NumberInput/component";
import { AutoCompleteLazy } from "@client/controls/Inputs/Select/AutoComplete/component";
import Select from "@client/controls/Inputs/Select/component";
import { usePromise } from "@client/lib/hooks";
import { joinDefined } from "@client/lib/misc";
import { type OptionItem, Status } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import { i18nFormat } from "@common/lib/i18n";
import getOptions from "@common/lib/options";
import { uniqBy } from "lodash";
import * as React from "react";
import { ModuleButton } from "../Button";
import api from "./api";
import { getDefaultSetting } from "./lib";
import { type NetworkPlacement } from "./types";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;
  error: string | null;

  settings: NetworkPlacement.Setting[];
  placementSettingOptions: OptionItem[];
  status: string;
  userId: number | null;
};

export type ActionProps = {
  save: (settings: NetworkPlacement.SettingSettings[]) => void;
};

const Content = (props: MapProps & ActionProps) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const [setting, setSetting] = React.useState<NetworkPlacement.Setting | null>(getDefaultSetting(props.settings));
  const [settings, updateSettings] = React.useState<NetworkPlacement.Setting[]>(props.settings);

  const [getResponse, isGetting] = usePromise({
    fn: api.get,
    args: [props.apiInfo, { genealogyTypeId: setting?.genealogyTypeId }],
    shouldLoad: !!setting?.genealogyTypeId && !!setting?.substituteParentUserId
  });
  const substituteSetting = React.useMemo(
    (): NetworkPlacement.SettingSettings | null => (getResponse ? getResponse.response?.data ?? null : null),
    [getResponse]
  );

  const genealogyTypeOptions = React.useMemo(
    (): OptionItem<string>[] =>
      uniqBy<NetworkPlacement.Setting>(props.settings, (x) => x.genealogyTypeName).map((x) => ({
        label: x.genealogyTypeName,
        value: x.genealogyTypeId
      })),
    [props.settings]
  );

  const fields = React.useMemo((): FormFieldInfo[] => {
    if (!props.settings.length || !setting) return [];

    const fields: FormFieldInfo[] = [];
    if (genealogyTypeOptions.length > 1) {
      fields.push({
        name: "genealogyTypeId",
        isRequired: true,
        label: i18n.genealogy,
        Component: Select,
        componentOpts: {
          options: genealogyTypeOptions,
          sort: "asc"
        },
        defaultValue: setting.genealogyTypeId
      });
    }

    fields.push({
      name: "substituteParentUserId",
      isRequired: false,
      label: i18n.substitute,
      Component: AutoCompleteLazy,
      componentOpts: {
        defaultSearchText: joinDefined(
          " ",
          setting.substituteParentUserFirstName,
          setting.substituteParentUserLastName
        ),
        dependent: setting.genealogyTypeId,
        fetchOptions: (x) =>
          options.getOptionsPage("user", "genealogies", {
            ...x,
            genealogyTypeId: setting.genealogyTypeId,
            userId: props.userId
          }),
        sort: "asc"
      },
      defaultValue: setting.substituteParentUserId
    });

    fields.push({
      name: "genealogyPlacementSettingKey",
      isDisabled: !!setting.substituteParentUserId,
      isRequired: true,
      label: i18n.settings,
      Component: Select,
      componentOpts: {
        isLoading: isGetting,
        options: props.placementSettingOptions,
        sort: "asc"
      },
      help: i18n.fe_Network_PlacementSettingsTooltip,
      defaultValue: substituteSetting?.genealogyPlacementSettingKey ?? setting.genealogyPlacementSettingKey
    });

    fields.push({
      name: "parentLeg",
      isDisabled: !!setting.substituteParentUserId,
      isRequired: true,
      label: i18n.defaultLeg,
      Component: NumberInput,
      defaultValue: substituteSetting?.parentLeg ?? setting.parentLeg
    });

    return fields;
  }, [
    props.apiInfo,
    genealogyTypeOptions,
    isGetting,
    i18n,
    props.placementSettingOptions,
    props.settings,
    props.userId,
    setting,
    substituteSetting
  ]);

  const onChange = React.useCallback(
    (name: keyof NetworkPlacement.SettingSettings, value: any) => {
      setSetting({ ...setting!, [name]: value });

      if (name === "genealogyTypeId") {
        setSetting(settings.find((x) => x.genealogyTypeId === value)!);
      }

      if (name === "genealogyPlacementSettingKey") {
        updateSettings(
          settings.map((x) => ({
            ...x,
            genealogyPlacementSettingKey:
              x.genealogyTypeId === setting?.genealogyTypeId ? value : x.genealogyPlacementSettingKey
          }))
        );
      }

      if (name === "parentLeg") {
        updateSettings(
          settings.map((x) => ({
            ...x,
            parentLeg:
              x.genealogyTypeId === setting?.genealogyTypeId &&
              x.genealogyPlacementSettingKey === setting.genealogyPlacementSettingKey
                ? value
                : x.parentLeg
          }))
        );
      }

      if (name === "substituteParentUserId") {
        updateSettings(
          settings.map((x) => ({
            ...x,
            substituteParentUserId:
              x.genealogyTypeId === setting?.genealogyTypeId &&
              x.genealogyPlacementSettingKey === setting.genealogyPlacementSettingKey
                ? value
                : x.substituteParentUserId
          }))
        );
      }
    },
    [setting, settings]
  );

  const submit = React.useCallback(
    () =>
      props.save(
        settings.map((x) => ({
          genealogyPlacementSettingKey: x.genealogyPlacementSettingKey,
          genealogyTypeId: x.genealogyTypeId,
          parentLeg: x.parentLeg,
          substituteParentUserId: x.substituteParentUserId
        }))
      ),
    [props.save, settings]
  );

  const FormButton = (innerProps: FormButtonProps) => (
    <ModuleButton
      {...getRootContainerProps(props)}
      isDisabled={props.status === Status.Saving}
      onClick={innerProps.ifValidated(submit)}
    />
  );

  const bottomHint = React.useMemo(() => {
    const minDefaultPlacements =
      settings.find((x) => x.genealogyTypeId === setting?.genealogyTypeId)?.genealogyTypeMinDefaultPlacements ?? 0;
    return minDefaultPlacements
      ? i18nFormat(i18n.fe_Network_PlacementCompanyPlacementInfo, ["N", minDefaultPlacements])
      : null;
  }, [settings, setting, i18n]);

  return (
    <FormContainer
      {...getRootContainerProps(props)}
      error={props.error}
      fields={fields}
      onChange={onChange}
      Button={FormButton}
      bottomHint={bottomHint}
    />
  );
};

export default (props: MapProps & ActionProps) => {
  const i18n = useI18n();

  return (
    <Container className="wsc_content_network_placement" {...getContainerProps(props)}>
      {!!props.settings.length && props.userId ? <Content {...props} /> : i18n.fe_Network_NoPlacementGenealogiesInfo}
    </Container>
  );
};
